import { invalidateCommunityQueryEventDispatch } from '@space-lib-events/invalidateCommunityQuery'
import { invalidateGroupCommunitiesEventDispatch } from '@space-lib-events/invalidateGroupCommunitiesQuery'
import { toastEventDispatch } from '@space-lib-events/toast'
import { ToastPosition, ToastType } from '@space-lib-events/toast/enum'
import { HttpCodes } from '@space-lib/constants'
import { CommunityTypeProductConsume, FailureDescription, TryResult } from '@space-lib/datahub'
import { DataHubEntities, DataHubService } from '@space-lib/datahub/hotmartClub'
import { useDeviceInfo } from '@space-lib/hooks'
import useMutation from '@space-shell/common/hooks/useMutation'
import { CommunitiesService } from '@space-shell/common/services/communities'

import { UserInfoRole } from '@space-shell/common/services/user'
import { useUserInfoStore } from '@space-shell/core/store'
import useCommunityConfigurationSidebar from '@space-shell/modules/communities/communityConfigurationSidebar/hooks/useConfigurationCommunitySidebar/useCommunityConfigurationSidebar'
import { useGetCommunity } from '@space-shell/modules/communities/hooks'
import TryToChangeCommunityTypeSchema from '@space-shell/modules/home/datahub/schemas/try/community/tryToChangeCommunityType'
import { useTranslation } from 'react-i18next'

import type { ChangeCommunityTypeMutationProps, ChangeCommunityTypeProps } from './types'
import { mapToChangeTypeLabelForDatahub } from './utils/mapToChangeTypeLabelForDatahub'
const useChangeCommunityTypeMutation = ({
  communityId,
  communityType,
  pendingType,
  setCommunityType,
  removeUsersOption,
  handleResetFields
}: ChangeCommunityTypeMutationProps) => {
  const deviceInfo = useDeviceInfo()
  const { invalidateCommunityData } = useGetCommunity(communityId)
  const { updateCommunity } = useCommunityConfigurationSidebar()
  const user = useUserInfoStore()
  const { t } = useTranslation()

  const changeCommunityTypeMutation = useMutation(
    ({ communityId, type, removeUsers }: ChangeCommunityTypeProps) =>
      CommunitiesService.changeCommunityType({ communityId, type, removeUsers })
  )

  const onConfirmButton = () => {
    if (!pendingType) return

    changeCommunityTypeMutation.mutate(
      { communityId: communityId, type: pendingType, removeUsers: removeUsersOption },
      {
        onSuccess: () => {
          invalidateCommunityQueryEventDispatch()
          invalidateCommunityData()
          setCommunityType(pendingType)
          updateCommunity({ isLocked: true, type: pendingType })
          handleResetFields()
          invalidateGroupCommunitiesEventDispatch()

          DataHubService.sendEvent({
            entity: DataHubEntities.Community,
            schema: new TryToChangeCommunityTypeSchema({
              deviceInfo,
              communityId: communityId,
              userRole: user.role || UserInfoRole.VIEWER,
              communityType: CommunityTypeProductConsume[communityType],
              changeType: mapToChangeTypeLabelForDatahub(communityType, pendingType),
              result: TryResult.SUCCESSFUL,
              statusCode: HttpCodes.OK,
              removeUsersOption,
              failureDescription: FailureDescription.NOT_FAILURE_RESULT_EVENT
            })
          })

          toastEventDispatch({
            type: ToastType.SUCCESS,
            message: t('configuration_sidebar.change_community_type_modal.success_alert_v2'),
            position: ToastPosition.BOTTOM_RIGHT
          })
        },
        onError: (error) => {
          DataHubService.sendEvent({
            entity: DataHubEntities.Community,
            schema: new TryToChangeCommunityTypeSchema({
              deviceInfo,
              userRole: user.role || UserInfoRole.VIEWER,
              communityId: communityId,
              communityType: CommunityTypeProductConsume[communityType],
              changeType: mapToChangeTypeLabelForDatahub(communityType, pendingType),
              result: TryResult.FAILURE,
              statusCode: error.statusCode,
              removeUsersOption,
              failureDescription: error.message ?? FailureDescription.NO_MESSAGE
            })
          })

          toastEventDispatch({
            type: ToastType.DANGER,
            message: t('configuration_sidebar.change_community_type_modal.error_alert'),
            position: ToastPosition.BOTTOM_RIGHT
          })
        }
      }
    )
  }

  return {
    onConfirmButton,
    ...changeCommunityTypeMutation
  }
}

export default useChangeCommunityTypeMutation
